import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { getDebugLogs, subscribeDebug, clearDebugLogs, type DebugLogEntry } from '@/utils/debugBus'
import { startConsoleCapture, getConsoleLogs, subscribeConsole, clearConsoleLogs, type ConsoleEntry } from '@/utils/consoleCapture'

/**
 * State and behaviour for the floating debug widget: the realtime debug-bus and
 * captured-console streams (with auto-scroll), the open/tab state and panel
 * height, the detail dialogs and copy/clear/format helpers. Extracted from
 * DebugWidget.vue so the component is just markup + styles.
 */
export function useDebugWidget() {
  const auth = useAuthStore()
  const route = useRoute()

  const enabled = computed(() => {
    // Enable debug widget in team routes or when enabled via auth config
    const isTeamRoute = String(route.name || '').startsWith('play-')
    return auth.debugWidgetEnabled || isTeamRoute
  })
  const open = ref(false)
  const logs = ref<DebugLogEntry[]>([])
  const consoleEntries = ref<ConsoleEntry[]>([])
  const tab = ref<'events' | 'console'>('events')
  const listRef = ref<HTMLDivElement | null>(null)
  const consoleListRef = ref<HTMLDivElement | null>(null)

  // Dialog states
  const showDetailsDialog = ref(false)
  const showConsoleDetailsDialog = ref(false)
  const selectedLogEntry = ref<DebugLogEntry | null>(null)
  const selectedConsoleEntry = ref<ConsoleEntry | null>(null)

  const routeName = computed(() => String(route.name || route.path || 'unknown'))
  const envLabel = computed(() => import.meta.env.PROD ? 'prod' : 'dev')
  const transportLabel = computed(() => {
    // Determine last transport seen in logs (WS or SSE)
    for (let i = logs.value.length - 1; i >= 0; i--) {
      const src = logs.value[i]?.source
      if (src === 'WS' || src === 'SSE') return src
    }
    return '—'
  })

  function setPanelHeight(active: boolean) {
    const val = active ? '40vh' : '0'
    document.documentElement.style.setProperty('--debug-panel-height', val)
  }

  function toggleOpen(val?: boolean) {
    open.value = typeof val === 'boolean' ? val : !open.value
  }

  function formatTs(ts: number) {
    const d = new Date(ts)
    return d.toLocaleTimeString()
  }

  function formatPayloadPreview(payload: any): string {
    if (!payload) return ''

    try {
      const str = typeof payload === 'string' ? payload : JSON.stringify(payload)
      // Show first 50 characters with ellipsis
      return str.length > 50 ? `${str.substring(0, 50)}...` : str
    } catch {
      return String(payload).substring(0, 50) + '...'
    }
  }

  function formatArgsPreview(args: any[]): string {
    const formatted = args.map(a => {
      if (typeof a === 'string') return a
      try {
        return JSON.stringify(a)
      } catch {
        return String(a)
      }
    }).join(' ')

    // Limit to 100 characters for one-line display
    return formatted.length > 100 ? `${formatted.substring(0, 100)}...` : formatted
  }

  function showLogDetails(entry: DebugLogEntry) {
    selectedLogEntry.value = entry
    showDetailsDialog.value = true
  }

  function showConsoleDetails(entry: ConsoleEntry) {
    selectedConsoleEntry.value = entry
    showConsoleDetailsDialog.value = true
  }

  function clearLogs() {
    clearDebugLogs()
    logs.value = []
  }

  function logsAsText(): string {
    return logs.value.map(l => {
      const ts = formatTs(l.ts)
      const meta = `[${ts}] ${l.level.toUpperCase()} ${l.source}: ${l.message}`
      let payload = ''
      if (l.payload !== undefined) {
        try { payload = `\n${JSON.stringify(l.payload, null, 2)}` } catch { payload = `\n${String(l.payload)}` }
      }
      return `${meta}${payload}`
    }).join('\n\n')
  }

  function consoleAsText(): string {
    return consoleEntries.value.map(c => {
      const ts = formatTs(c.ts)
      const meta = `[${ts}] ${c.level.toUpperCase()}`
      return `${meta}\n${formatArgs(c.args)}`
    }).join('\n\n')
  }

  function formatArgs(args: any[]): string {
    return args.map(a => {
      if (typeof a === 'string') return a
      try { return JSON.stringify(a, null, 2) } catch { return String(a) }
    }).join(' ')
  }

  const activeTabIsConsole = computed(() => tab.value === 'console')

  function clearActive() {
    if (activeTabIsConsole.value) {
      clearConsoleLogs()
      consoleEntries.value = []
    } else {
      clearLogs()
    }
  }

  async function copyActive() {
    const text = activeTabIsConsole.value ? consoleAsText() : logsAsText()
    try { await navigator.clipboard.writeText(text) } catch {
      try {
        const el = document.createElement('textarea')
        el.value = text
        el.style.position = 'fixed'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select(); document.execCommand('copy')
        document.body.removeChild(el)
      } catch {
        // Ignore clipboard errors
      }
    }
  }

  let unsubscribe: (() => void) | null = null
  let unsubConsole: (() => void) | null = null
  onMounted(() => {
    logs.value = getDebugLogs()
    // start console capture as the widget mounts (also started globally in App for early logs)
    startConsoleCapture()
    consoleEntries.value = getConsoleLogs()
    unsubscribe = subscribeDebug((entry) => {
      logs.value = [...logs.value, entry]
      // Auto scroll to bottom
      nextTick(() => {
        const el = listRef.value
        if (el) el.scrollTop = el.scrollHeight
      })
    })
    unsubConsole = subscribeConsole((entry) => {
      consoleEntries.value = [...consoleEntries.value, entry]
      nextTick(() => {
        const el = consoleListRef.value
        if (el) el.scrollTop = el.scrollHeight
      })
    })
  })

  onBeforeUnmount(() => {
    if (unsubscribe) unsubscribe()
    if (unsubConsole) unsubConsole()
    setPanelHeight(false)
  })

  // Keep main area clear of the panel
  watch(open, (val) => setPanelHeight(val), { immediate: true })

  return {
    enabled,
    open,
    logs,
    consoleEntries,
    tab,
    listRef,
    consoleListRef,
    showDetailsDialog,
    showConsoleDetailsDialog,
    selectedLogEntry,
    selectedConsoleEntry,
    routeName,
    envLabel,
    transportLabel,
    activeTabIsConsole,
    toggleOpen,
    copyActive,
    clearActive,
    showLogDetails,
    showConsoleDetails,
    formatTs,
    formatPayloadPreview,
    formatArgsPreview,
  }
}
